import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/design-system/select';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import {
  ClickSeriesPoint,
  ConversionOverview,
} from '@/services/dashboard/conversionAnalytics/conversionAnalytics.types';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getVisibleClickData } from '../utils/getVisibleClickData';
import { ConversionGraphSkeleton } from './OverviewSkeletons';

interface TodayClicksAnalyticsProps {
  data?: ConversionOverview;
  channel: string;
  onChannelChange: (channel: string) => void;
  isLoading?: boolean;
}

const areaColors = [
  'var(--chart-clicks-1)',
  'var(--chart-clicks-2)',
  'var(--chart-clicks-3)',
  'var(--chart-clicks-4)',
  'var(--chart-clicks-5)',
];
const STORE_PAGE = 'store_page';

export const TodayClicksAnalytics = ({
  data,
  channel,
  onChannelChange,
  isLoading = false,
}: TodayClicksAnalyticsProps) => {
  const { locale } = useDynamicLocale();

  if (isLoading) return <ConversionGraphSkeleton />;
  const clickData = data?.clickSeries.points ?? [];
  const visibleClickData = getVisibleClickData(clickData);
  const hasClickData = visibleClickData.some(({ value }) => value > 0);
  const xAxisTicks = visibleClickData.filter(({ time }) => time.endsWith(':00')).map(({ time }) => time);
  const actionSources = data?.actionSourceByPage ?? [];
  const clickOrigins = data?.clickOriginByArea ?? [];

  return (
    <div className="flex flex-col lg:flex-row gap-6 mt-6 w-full">
      <div className="flex-1 bg-card rounded-sm border border-border-subtle p-6 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-foreground-heading">
            <Translation id="dashboard.conversion.page.todaysClick" />
          </h2>
          <Select value={channel} onValueChange={onChannelChange}>
            <SelectTrigger className="w-[140px] text-xs font-medium border border-border-subtle">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <Translation id="dashboard.conversion.page.all" />
              </SelectItem>
              <SelectItem value="phone">
                <Translation id="dashboard.conversion.page.phoneCall" />
              </SelectItem>
              <SelectItem value="reservation">
                <Translation id="dashboard.conversion.page.reservationClicks" />
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {hasClickData ? (
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={visibleClickData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="time"
                  axisLine={false}
                  tickLine={false}
                  ticks={xAxisTicks}
                  interval={0}
                  angle={0}
                  textAnchor="middle"
                  height={30}
                  tickMargin={8}
                  tick={{
                    fill: 'var(--chart-axis)',
                    fontSize: 'var(--text-micro)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  tickMargin={8}
                  tick={{
                    fill: 'var(--chart-axis)',
                    fontSize: 'var(--text-micro)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                  }}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const itemData = payload[0].payload as ClickSeriesPoint;
                      return (
                        <div className="bg-tooltip-inverse text-on-color text-xs px-2 py-1 rounded shadow">
                          <p>{`${itemData.time} : ${String(payload[0].value ?? 0)} clicks`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="value" fill="var(--chart-clicks-1)" radius={[2, 2, 0, 0]} barSize={5} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex h-[250px] items-center justify-center rounded-md border border-empty-border bg-empty-background px-6 text-center">
            <div>
              <p className="text-lg font-semibold text-empty-foreground">
                <Translation id="dashboard.conversion.page.noClickData" />
              </p>
              <p className="mt-2 text-sm text-empty-description">
                <Translation id="dashboard.conversion.page.noDataToShow" />
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Side widgets: Action Source & Click Origin */}
      <div className="w-full lg:w-[320px] bg-card rounded-sm border border-border-subtle p-6 flex flex-col justify-between">
        <div>
          <h2 className="text-sm font-bold text-foreground-heading mb-4">
            <Translation id="dashboard.conversion.page.actionSourceByPage" />
          </h2>
          <div className="space-y-4">
            {actionSources.map(({ label, count }) => (
              <div key={label}>
                <div className="flex justify-between text-xs font-semibold mb-1 text-foreground-detail">
                  <span>
                    {label === STORE_PAGE ? (
                      <Translation id="dashboard.conversion.page.storePage" />
                    ) : (
                      <Translation id="dashboard.conversion.page.hostDetailPage" />
                    )}
                  </span>
                  <span>{count}%</span>
                </div>
                <div className="w-full h-[6px] bg-surface-sunken rounded-full overflow-hidden">
                  <div
                    className="h-full bg-chart-clicks-1 rounded-full"
                    style={{ width: `${Math.min(Math.max(count, 0), 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border-soft my-6" />

        <div>
          <h2 className="text-sm font-bold text-foreground-heading mb-4">
            <Translation id="dashboard.conversion.page.clickOriginByArea" />
          </h2>
          <div className="space-y-3">
            {clickOrigins.map(({ area, label, count }, index) => (
              <div
                key={label}
                className="flex items-center justify-between text-xs font-semibold text-foreground-detail"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="size-2 rounded-full"
                    style={{ backgroundColor: areaColors[index % areaColors.length] }}
                  />
                  <span>{locale === 'en' ? label : area}</span>
                </div>
                <span>{count}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
